"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PassageDetailsContainer = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const passage_details_1 = require("../../model/passage-details");
const material_1 = require("@mui/material");
const react_2 = __importDefault(require("react"));
const paragraph_block_renderer_1 = require("./paragraph/paragraph-block-renderer");
const image_block_renderer_1 = require("./image/image-block-renderer");
const audio_player_1 = require("../../component/audio/audio-player");
const passage_meta_1 = require("../../model/passage-meta");
const use_lazy_promise_1 = require("../../hook/use-lazy-promise");
const passage_details_2 = require("../../network/passage-details");
const alert_context_1 = require("../../context/alert-context");
const user_context_1 = require("../../context/user-context");
const feedback_fab_1 = require("./feedback/feedback-fab");
const audio_button_1 = require("./audio/audio-button");
const button_1 = require("../../component/input/button");
const log_1 = require("../../model/log");
exports.PassageDetailsContainer = (0, react_1.memo)(({ details, mode, onQuiz, onRead }) => {
    const audioRef = react_2.default.useRef(null);
    const [time, setTime] = react_2.default.useState(0);
    const [isPlaying, setPlaying] = react_2.default.useState(true);
    const theme = (0, material_1.useTheme)();
    const alert = (0, alert_context_1.useAlert)();
    const { status: updateListenStatusStatus, error: updateListenStatusError, execute: updateListenStatusExecute, } = (0, use_lazy_promise_1.useLazyPromise)(passage_details_2.updateListenStatus);
    const { refresh: refreshUserMeta } = (0, user_context_1.useUserUpdate)();
    const headerRef = (0, react_1.useRef)(null);
    const buttonRef = (0, react_1.useRef)(null);
    const { status: updateReadStatusStatus, error: updateReadStatusError, execute: updateReadStatusExecute, } = (0, use_lazy_promise_1.useLazyPromise)(passage_details_2.updateReadStatus);
    const onTimeUpdate = (0, react_1.useCallback)((time) => {
        setTime(time);
    }, []);
    const paragraphIndex = react_2.default.useMemo(() => {
        let currentIndex = 0;
        for (let i = details.blocks.length - 1; i >= 0; i--) {
            const block = details.blocks[i];
            if (block.type === passage_details_1.PassageBlockType.Paragraph) {
                const paragraphBlock = block;
                if (paragraphBlock.sentences.length > 0) {
                    const lastSentence = paragraphBlock.sentences[paragraphBlock.sentences.length - 1];
                    if (time >= lastSentence.audioOffset + lastSentence.audioDuration) {
                        currentIndex = i + 1;
                        break; // Found the last paragraph that has been played
                    }
                }
            }
        }
        return details.blocks.findIndex((block, index) => block.type === passage_details_1.PassageBlockType.Paragraph && index >= currentIndex);
    }, [details.blocks, time]);
    const onSelect = (0, react_1.useCallback)((audioOffset) => {
        if (audioRef.current) {
            audioRef.current.updateProgress(audioOffset);
        }
    }, []);
    const handleStartQuiz = (0, react_1.useCallback)(() => {
        onQuiz();
    }, [onQuiz]);
    const handleAudioEnd = (0, react_1.useCallback)(() => {
        setPlaying(false);
        if (mode === passage_meta_1.PassageLearningMode.LISTEN &&
            details.meta.status.listen !== passage_meta_1.PassageTaskStatus.COMPLETED) {
            updateListenStatusExecute(details.meta.id, passage_meta_1.PassageTaskStatus.COMPLETED);
        }
    }, [
        details.meta.id,
        details.meta.status.listen,
        mode,
        updateListenStatusExecute,
    ]);
    const scrollToTop = (0, react_1.useCallback)(() => {
        var _a;
        (_a = headerRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }, []);
    const handleRead = (0, react_1.useCallback)(() => {
        onRead();
    }, [onRead]);
    (0, react_1.useEffect)(() => {
        if (mode !== passage_meta_1.PassageLearningMode.READ ||
            !buttonRef.current ||
            updateReadStatusStatus === use_lazy_promise_1.FetchStatus.Success ||
            details.meta.status.read === passage_meta_1.PassageTaskStatus.COMPLETED) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            var _a;
            if ((_a = entries.find((entry) => entry.target === buttonRef.current)) === null || _a === void 0 ? void 0 : _a.isIntersecting) {
                updateReadStatusExecute(details.meta.id, passage_meta_1.PassageTaskStatus.COMPLETED);
            }
        });
        observer.observe(buttonRef.current);
    }, [
        details.meta.id,
        details.meta.status.read,
        mode,
        updateReadStatusExecute,
        updateReadStatusStatus,
    ]);
    (0, react_1.useEffect)(() => {
        if (audioRef.current && mode === passage_meta_1.PassageLearningMode.LISTEN) {
            audioRef.current.updateProgress(0);
        }
    }, [details.audioUrl, mode]);
    (0, react_1.useEffect)(() => {
        if (mode === passage_meta_1.PassageLearningMode.READ) {
            scrollToTop();
        }
    }, [mode, scrollToTop]);
    (0, react_1.useEffect)(() => {
        if (updateListenStatusStatus === use_lazy_promise_1.FetchStatus.Error) {
            alert.showAlert((updateListenStatusError === null || updateListenStatusError === void 0 ? void 0 : updateListenStatusError.message) || "Failed to update listen status", alert_context_1.AlertType.Error);
        }
        else if (updateListenStatusStatus === use_lazy_promise_1.FetchStatus.Success) {
            refreshUserMeta();
        }
    }, [
        updateListenStatusStatus,
        updateListenStatusError,
        alert,
        refreshUserMeta,
    ]);
    (0, react_1.useEffect)(() => {
        if (updateReadStatusStatus === use_lazy_promise_1.FetchStatus.Error) {
            alert.showAlert((updateReadStatusError === null || updateReadStatusError === void 0 ? void 0 : updateReadStatusError.message) || "Failed to update read status", alert_context_1.AlertType.Error);
        }
        else if (updateReadStatusStatus === use_lazy_promise_1.FetchStatus.Success) {
            refreshUserMeta();
        }
    }, [updateReadStatusStatus, updateReadStatusError, alert, refreshUserMeta]);
    const handleFeedbackOpen = (0, react_1.useCallback)(() => {
        var _a;
        (_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.pause();
    }, []);
    const handlePlayStateChange = (0, react_1.useCallback)((isPlaying) => {
        setPlaying(isPlaying);
    }, []);
    const handleAudioButtonClick = (0, react_1.useCallback)(() => {
        var _a, _b;
        if (isPlaying) {
            (_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.pause();
        }
        else {
            (_b = audioRef.current) === null || _b === void 0 ? void 0 : _b.play();
        }
    }, [isPlaying]);
    return ((0, jsx_runtime_1.jsxs)(material_1.Stack, { spacing: 2, padding: 4, direction: "column", children: [(0, jsx_runtime_1.jsx)(audio_player_1.AudioPlayer, { src: details.audioUrl, ref: audioRef, onTimeUpdate: onTimeUpdate, autoPlay: mode === passage_meta_1.PassageLearningMode.LISTEN, onEnded: handleAudioEnd, onPlayStateChange: handlePlayStateChange }), (0, jsx_runtime_1.jsx)(audio_button_1.AudioButton, { onClick: handleAudioButtonClick, isPlaying: isPlaying }), (0, jsx_runtime_1.jsx)(feedback_fab_1.FeedbackFab, { onOpen: handleFeedbackOpen, currentPlayTime: time, passageDetails: details }), (0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "h4", fontWeight: theme.typography.fontWeightBold, color: "text.primary", ref: headerRef, children: details.meta.title }), details.blocks.map((block, index) => {
                switch (block.type) {
                    case passage_details_1.PassageBlockType.Paragraph:
                        return ((0, jsx_runtime_1.jsx)(paragraph_block_renderer_1.ParagraphBlockRenderer, { block: block, currentPlayTime: time, onSelect: onSelect, isCurrentParagraph: index === paragraphIndex }, index));
                    case passage_details_1.PassageBlockType.Image:
                        return ((0, jsx_runtime_1.jsx)(image_block_renderer_1.ImageBlockRenderer, { block: block }, index));
                    default:
                        return null;
                }
            }), mode === passage_meta_1.PassageLearningMode.LISTEN ? ((0, jsx_runtime_1.jsx)(button_1.Button, { color: "primary", onClick: handleRead, variant: "contained", eventName: log_1.LogEventClickName.ReadPassage, sx: { alignSelf: "flex-start", width: theme.spacing(20) }, disabled: updateListenStatusStatus !== use_lazy_promise_1.FetchStatus.Success &&
                    details.meta.status.listen !== passage_meta_1.PassageTaskStatus.COMPLETED, startIcon: updateListenStatusStatus === use_lazy_promise_1.FetchStatus.Loading ? ((0, jsx_runtime_1.jsx)(material_1.CircularProgress, { sx: { width: theme.spacing(2), height: theme.spacing(2) }, size: "small", color: "secondary" })) : null, children: "Read Passage" })) : ((0, jsx_runtime_1.jsx)(button_1.Button, { color: "primary", onClick: handleStartQuiz, variant: "contained", eventName: log_1.LogEventClickName.StartQuiz, size: "large", ref: buttonRef, sx: { alignSelf: "flex-start", width: theme.spacing(20) }, disabled: updateReadStatusStatus !== use_lazy_promise_1.FetchStatus.Success &&
                    details.meta.status.read !== passage_meta_1.PassageTaskStatus.COMPLETED, startIcon: updateReadStatusStatus === use_lazy_promise_1.FetchStatus.Loading ? ((0, jsx_runtime_1.jsx)(material_1.CircularProgress, { sx: { width: theme.spacing(2), height: theme.spacing(2) }, size: "small", color: "secondary" })) : null, children: "Start Quiz" }))] }));
});
exports.PassageDetailsContainer.displayName = "PassageDetailsContainer";
